import type { ReactNode } from 'react';

/**
 * One stroke weight, one grid, one join style. A mixed icon set is the fastest way
 * to make an otherwise careful interface look assembled rather than designed.
 */

export type IconProps = { size?: number; className?: string; strokeWidth?: number };

const icon =
  (path: ReactNode) =>
  ({ size = 18, className, strokeWidth = 1.6 }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );

export const IconShield = icon(
  <>
    <path d="M12 3 5 6v5.5c0 4.2 2.9 8.1 7 9.5 4.1-1.4 7-5.3 7-9.5V6l-7-3Z" />
    <path d="m9 12 2 2 4-4" />
  </>,
);

export const IconOverview = icon(
  <>
    <path d="M4 20h16" />
    <path d="M7 20v-6M12 20V7M17 20v-9" />
  </>,
);

export const IconInspections = icon(
  <>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <path d="m4 6 1 1 1.5-2M4 12l1 1 1.5-2M4 18l1 1 1.5-2" />
  </>,
);

export const IconReports = icon(
  <>
    <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Z" />
    <path d="M14 3v4h4" />
    <path d="M9.5 13h5M9.5 16.5h3" />
  </>,
);

export const IconRooms = icon(
  <>
    <path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" />
    <path d="M4 21h16" />
    <circle cx="14.5" cy="12" r="1" fill="currentColor" stroke="none" />
  </>,
);

export const IconSupervisors = icon(
  <>
    <circle cx="9" cy="8.5" r="3" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 6.2a3 3 0 0 1 0 5.6" />
    <path d="M17.5 14.4a5.5 5.5 0 0 1 3 4.6" />
  </>,
);

export const IconAudit = icon(
  <>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
    <path d="M3.5 4.5V9H8" />
    <path d="M12 8v4.5l3 1.7" />
  </>,
);

export const IconSettings = icon(
  <>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </>,
);

export const IconSearch = icon(
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </>,
);

export const IconFilter = icon(<path d="M4 5h16l-6.2 7.2V19l-3.6 2v-8.8L4 5Z" />);

export const IconCalendar = icon(
  <>
    <rect x="3.5" y="5" width="17" height="16" rx="2" />
    <path d="M3.5 10h17M8 3.5V6M16 3.5V6" />
  </>,
);

export const IconChevron = icon(<path d="m9 5 7 7-7 7" />);
export const IconChevronDown = icon(<path d="m6 9 6 6 6-6" />);
export const IconArrowRight = icon(
  <>
    <path d="M4 12h15" />
    <path d="m13 6 6 6-6 6" />
  </>,
);

export const IconClose = icon(<path d="M6 6l12 12M18 6 6 18" />);
export const IconMenu = icon(<path d="M4 7h16M4 12h16M4 17h16" />);
export const IconUser = icon(
  <>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </>,
);
export const IconLogout = icon(
  <>
    <path d="M14 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4" />
    <path d="M10 8 6 12l4 4" />
    <path d="M6 12h9" />
  </>,
);

export const IconPrint = icon(
  <>
    <path d="M7 9V4h10v5" />
    <path d="M7 18H5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2" />
    <rect x="7" y="15" width="10" height="6" rx="1" />
  </>,
);

export const IconPencil = icon(
  <>
    <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="m14.5 6.5 3 3" />
  </>,
);

export const IconCamera = icon(
  <>
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </>,
);

export const IconClock = icon(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.75" />
  </>,
);

export const IconCheck = icon(<path d="m5 13 4 4L19 7" />);

export const IconCheckCircle = icon(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="m8.5 12 2.5 2.5 4.5-5" />
  </>,
);

export const IconAlert = icon(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5v5M12 16h.01" />
  </>,
);

export const IconInfo = icon(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 8h.01" />
  </>,
);

export const IconRefresh = icon(
  <>
    <path d="M20 11A8 8 0 0 0 6.3 6.3L4 8.5" />
    <path d="M4 4v4.5h4.5" />
    <path d="M4 13a8 8 0 0 0 13.7 4.7L20 15.5" />
    <path d="M20 20v-4.5h-4.5" />
  </>,
);

export const IconExpand = icon(
  <>
    <path d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5" />
  </>,
);

export const IconBath = icon(
  <>
    <path d="M4 12h16v2a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-2Z" />
    <path d="M7 12V6a2 2 0 0 1 4 0" />
    <path d="M6 19l-1 2M18 19l1 2" />
  </>,
);

export const IconKitchen = icon(
  <>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M4 10h16" />
    <path d="M8 6.5h.01M8 14h.01" />
  </>,
);

export const IconBed = icon(
  <>
    <path d="M3 18V7" />
    <path d="M3 12h18v6" />
    <path d="M21 18v-4a2 2 0 0 0-2-2" />
    <circle cx="7.5" cy="9.5" r="1.75" />
  </>,
);
